import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { formatPercent, handleApiError } from '~/utils/helper';
import { IProjectReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetClosingRateReport } from '~/thunks/report/reportThunk';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';

const ClosingRateReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];

  // Dynamic columns for job types
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'staffMember', label: t('properties.staffMember') },

      { id: 'total', label: t('properties.total') },
      { id: 'prjWon', label: t('properties.prjWon') },
      { id: 'prjLost', label: t('properties.prjLost') },
      { id: 'closingRate', label: t('properties.closingRate') },
    ],
    [t]
  );
  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [job, setJob] = useState<Array<IProjectReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];

  const handleSearch = () => {
    if (selectedAccount.includes('all')) {
      dispatch(handleGetClosingRateReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setJob(response.result.report || []);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetClosingRateReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setJob(response.result.report || []);
        })
        .catch(handleApiError);
    }
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  useEffect(() => {
    dispatch(handleGetClosingRateReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);

        setSelectedAccount(['all']);
        setJob(response.result.report || []);
      })
      .catch(handleApiError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.closingRate')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.staffMember')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.staffMember')}
            placeholder={t('properties.staffMember')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[640px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='w-[9.09%] bg-[#F0F7FF] text-[#758CA5] text-sm font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {job.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[9.09%]'>{row.staffMember}</TableCell>

                <TableCell className='w-[9.09%]'>{row.total}</TableCell>
                <TableCell className='w-[9.09%]'>{row.projectWon}</TableCell>
                <TableCell className='w-[9.09%]'>{row.projectLost}</TableCell>
                <TableCell className='w-[9.09%]'>{formatPercent(row.closingRate)}</TableCell>
                {/* Render job types dynamically */}
              </TableRow>
            ))}
          </TableBody>
          {/* Total row */}
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.filter((row) => row.staffMember).length}
              </TableCell>

              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.total || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.projectWon || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {job.reduce((sum, row) => sum + (row.projectLost || 0), 0)}
              </TableCell>
              <TableCell className='w-[9.09%] font-bold text-[#758CA5]'>
                {formatPercent(job.reduce((sum, row) => sum + (row.closingRate || 0), 0) / job.length)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ClosingRateReport;
